import { parseCSV } from './csv';
import { normalizeManagerName } from './managerStats';

export function normalizeBureauName(name) {
  return normalizeManagerName(String(name || '').trim());
}

function num(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function yearOf(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
}

function round(value, places = 2) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

function isPlayoffChampion(method) {
  return /Playoff Champion/i.test(method || '');
}

export function collectWeekScores(matchupRows = []) {
  const weeks = new Map();
  matchupRows.forEach((row) => {
    const year = yearOf(row.Year || row.Season);
    const week = yearOf(row.Week);
    const team1 = normalizeBureauName(row['Team1 Manager']);
    const team2 = normalizeBureauName(row['Team2 Manager']);
    const score1 = num(row['Team1 Score']);
    const score2 = num(row['Team2 Score']);
    if (!year || !week || !team1 || !team2 || score1 === null || score2 === null) return;
    const key = `${year}-${week}`;
    if (!weeks.has(key)) weeks.set(key, { year, week, entries: [] });
    const bucket = weeks.get(key);
    bucket.entries.push({ manager: team1, score: score1, opponent: team2, opponentScore: score2 });
    bucket.entries.push({ manager: team2, score: score2, opponent: team1, opponentScore: score1 });
  });
  return Array.from(weeks.values());
}

export function fieldResultsForWeek(week) {
  const scores = week.entries;
  const fieldSize = scores.length - 1;
  return scores.map((entry) => {
    const fieldWins = scores.filter((other) => other.manager !== entry.manager && other.score < entry.score).length;
    const fieldLosses = scores.filter((other) => other.manager !== entry.manager && other.score > entry.score).length;
    const fieldTies = scores.filter((other) => other.manager !== entry.manager && other.score === entry.score).length;
    const result = entry.score > entry.opponentScore ? 'W' : entry.score < entry.opponentScore ? 'L' : 'T';
    const expectedWin = fieldSize > 0 ? fieldWins / fieldSize : 0;
    const actualWin = result === 'W' ? 1 : result === 'T' ? 0.5 : 0;
    return {
      year: week.year,
      week: week.week,
      manager: entry.manager,
      score: entry.score,
      opponent: entry.opponent,
      opponentScore: entry.opponentScore,
      result,
      fieldWins,
      fieldLosses,
      fieldTies,
      fieldSize,
      expectedWin,
      actualWin,
      luckyWin: result === 'W' && fieldWins <= 2,
      unluckyLoss: result === 'L' && fieldLosses <= 1,
      soloLucky: result === 'W' && fieldWins === 1,
      brutalLoss: result === 'L' && fieldLosses === 1,
    };
  });
}

export function buildLuckTribunal(matchupRows = []) {
  const weeks = collectWeekScores(matchupRows);
  const results = weeks.flatMap(fieldResultsForWeek);
  const byManager = new Map();
  results.forEach((row) => {
    if (!byManager.has(row.manager)) {
      byManager.set(row.manager, {
        manager: row.manager,
        games: 0,
        actualWins: 0,
        expectedWins: 0,
        luckyWins: 0,
        unluckyLosses: 0,
        soloLucky: 0,
        brutalLosses: 0,
      });
    }
    const career = byManager.get(row.manager);
    career.games += 1;
    career.actualWins += row.actualWin;
    career.expectedWins += row.expectedWin;
    if (row.luckyWin) career.luckyWins += 1;
    if (row.unluckyLoss) career.unluckyLosses += 1;
    if (row.soloLucky) career.soloLucky += 1;
    if (row.brutalLoss) career.brutalLosses += 1;
  });

  const careers = Array.from(byManager.values()).map((row) => ({
    ...row,
    actualWins: round(row.actualWins),
    expectedWins: round(row.expectedWins),
    luckWins: round(row.actualWins - row.expectedWins),
  }));

  careers.sort((first, second) => first.luckWins - second.luckWins);

  return {
    weekCount: weeks.length,
    gameCount: weeks.reduce((sum, week) => sum + week.entries.length / 2, 0),
    careers,
    unluckiest: careers[0] || null,
    luckiest: careers[careers.length - 1] || null,
    brutalBoard: careers.slice().sort((first, second) => second.brutalLosses - first.brutalLosses),
    unluckyLosses: results.filter((row) => row.brutalLoss).sort((first, second) => second.score - first.score),
    luckyWins: results.filter((row) => row.soloLucky).sort((first, second) => first.score - second.score),
  };
}

export function buildSackoLineage(standingsRows = []) {
  const byYear = new Map();
  standingsRows.forEach((row) => {
    const year = yearOf(row.Year);
    const manager = normalizeBureauName(row.Manager);
    const wins = num(row.Wins);
    const losses = num(row.Losses);
    const pointsFor = num(row['Points For']);
    if (!year || !manager || wins === null || losses === null) return;
    if (!byYear.has(year)) byYear.set(year, []);
    byYear.get(year).push({
      year,
      manager,
      team: row.Team || '',
      wins,
      losses,
      pointsFor: pointsFor || 0,
    });
  });

  const lineage = Array.from(byYear.keys()).sort((a, b) => a - b).map((year) => {
    const ranked = byYear.get(year).slice().sort((first, second) => first.wins - second.wins || first.pointsFor - second.pointsFor);
    const holder = ranked[0];
    return {
      ...holder,
      record: `${holder.wins}-${holder.losses}`,
    };
  });

  const counts = new Map();
  lineage.forEach((row) => {
    counts.set(row.manager, (counts.get(row.manager) || 0) + 1);
  });
  const leaders = Array.from(counts.entries())
    .map(([manager, count]) => ({ manager, count }))
    .sort((first, second) => second.count - first.count || first.manager.localeCompare(second.manager));

  return { lineage, leaders, king: leaders[0] || null };
}

export function buildCurseBoard(standingsRows = [], championshipRows = []) {
  const ringYears = new Set(
    championshipRows
      .filter((row) => isPlayoffChampion(row.Method))
      .map((row) => `${yearOf(row.Year)}:${normalizeBureauName(row.Champion)}`),
  );

  const seasons = standingsRows.map((row) => {
    const year = yearOf(row.Year);
    const manager = normalizeBureauName(row.Manager);
    const wins = num(row.Wins);
    const losses = num(row.Losses);
    return {
      year,
      manager,
      team: row.Team || '',
      wins: wins || 0,
      losses: losses || 0,
      pointsFor: num(row['Points For']) || 0,
      playoffs: String(row['Playoffs Made']).toLowerCase() === 'true',
    };
  }).filter((row) => row.year && row.manager);

  const regularSeasonCrowns = championshipRows
    .filter((row) => /Regular Season Champion/i.test(row.Method || ''))
    .map((row) => ({
      year: yearOf(row.Year),
      manager: normalizeBureauName(row.Champion),
      team: row.Team || '',
      record: row.Record || '',
      method: row.Method,
      wonRing: ringYears.has(`${yearOf(row.Year)}:${normalizeBureauName(row.Champion)}`),
    }))
    .filter((row) => row.year && !row.wonRing);

  const tenWinDroughts = seasons.filter((row) => (
    row.wins >= 10 && !ringYears.has(`${row.year}:${row.manager}`)
  ));

  const byManager = new Map();
  function add(manager, kind, season) {
    if (!byManager.has(manager)) {
      byManager.set(manager, { manager, regularSeasonCrowns: 0, tenWinDroughts: 0, seasons: [] });
    }
    const entry = byManager.get(manager);
    if (kind === 'crown') entry.regularSeasonCrowns += 1;
    if (kind === 'ten') entry.tenWinDroughts += 1;
    entry.seasons.push(season);
  }
  regularSeasonCrowns.forEach((row) => add(row.manager, 'crown', row));
  tenWinDroughts.forEach((row) => add(row.manager, 'ten', row));

  const conversion = [];
  const playoffSeasons = seasons.filter((row) => row.playoffs);
  const playoffByManager = new Map();
  playoffSeasons.forEach((row) => {
    playoffByManager.set(row.manager, (playoffByManager.get(row.manager) || 0) + 1);
  });
  const ringsByManager = new Map();
  championshipRows.filter((row) => isPlayoffChampion(row.Method)).forEach((row) => {
    const manager = normalizeBureauName(row.Champion);
    ringsByManager.set(manager, (ringsByManager.get(manager) || 0) + 1);
  });
  playoffByManager.forEach((appearances, manager) => {
    const rings = ringsByManager.get(manager) || 0;
    conversion.push({
      manager,
      appearances,
      rings,
      conversion: appearances ? round((rings / appearances) * 100, 1) : 0,
    });
  });
  conversion.sort((first, second) => first.conversion - second.conversion || second.appearances - first.appearances);

  const cursed = Array.from(byManager.values()).sort((first, second) => (
    (second.regularSeasonCrowns + second.tenWinDroughts) - (first.regularSeasonCrowns + first.tenWinDroughts)
  ));

  return {
    regularSeasonCrowns,
    tenWinDroughts,
    cursed,
    conversion,
    cursedKing: cursed[0] || null,
  };
}

export function buildAwardsShow({ luck, sacko, curse, closestLoss, highestScoringLoss }) {
  const appointed = [
    {
      id: 'glitch',
      title: 'Glitch Champion',
      winner: 'Kevin',
      value: '5.30',
      copy: 'Won 2023 by 5.30 points, then went 2-12 and 2-12. The Bureau keeps the ring in a drawer labeled bubble.',
    },
    {
      id: 'cheekwood',
      title: 'Mouth of the League, Unofficial',
      winner: 'Josh',
      value: 'Daddy',
      copy: 'Loudest available owner. One finals appearance. A Cheekwood no-show. The nickname does more work than the roster.',
    },
    {
      id: 'team-name',
      title: 'Best Team Name, Forever',
      winner: 'Kyle',
      value: 'RobTop Kevin Bottom',
      copy: '2015. Still undefeated as a sentence. The rest of the league has been chasing this joke for a decade.',
    },
    {
      id: 'homeowner',
      title: 'Homeowner Drop',
      winner: 'Robby',
      value: '1.4%',
      copy: 'Teichmann model line for 2026. AJ seconded the hose-bib theory. The Bureau files it under infrastructure.',
    },
  ];

  const computed = [
    luck?.unluckiest && {
      id: 'unluckiest',
      title: 'Unluckiest Manager',
      winner: luck.unluckiest.manager,
      value: `${luck.unluckiest.luckWins > 0 ? '+' : ''}${luck.unluckiest.luckWins}`,
      copy: `Actual wins minus play-the-field expected wins. ${luck.unluckiest.manager} has been the schedule's favorite victim.`,
    },
    luck?.luckiest && {
      id: 'luckiest',
      title: 'Luckiest Manager',
      winner: luck.luckiest.manager,
      value: `${luck.luckiest.luckWins > 0 ? '+' : ''}${luck.luckiest.luckWins}`,
      copy: 'Same math, other direction. The Bureau does not allege witchcraft. It alleges a friendly schedule.',
    },
    sacko?.king && {
      id: 'sacko-king',
      title: 'Sacko King',
      winner: sacko.king.manager,
      value: String(sacko.king.count),
      copy: `Last-place finishes in the regular-season standings. ${sacko.king.manager} has lifted the cursed object ${sacko.king.count} times.`,
    },
    curse?.cursedKing && {
      id: 'curse-king',
      title: 'Regular-Season Curse',
      winner: curse.cursedKing.manager,
      value: String(curse.cursedKing.regularSeasonCrowns),
      copy: 'Regular-season crowns, zero playoff rings attached. A beautiful resume with no jewelry.',
    },
    closestLoss && {
      id: 'heartbreak',
      title: 'Heartbreak Award',
      winner: closestLoss.loser,
      value: String(closestLoss.margin),
      copy: `${closestLoss.year} Week ${closestLoss.week}: ${closestLoss.winner} ${closestLoss.winnerScore}, ${closestLoss.loser} ${closestLoss.loserScore}.`,
    },
    highestScoringLoss && {
      id: 'high-loss',
      title: 'Highest-Scoring Loss',
      winner: highestScoringLoss.loser,
      value: String(highestScoringLoss.loserScore),
      copy: `${highestScoringLoss.year} Week ${highestScoringLoss.week} vs ${highestScoringLoss.winner}. Put up a video-game number and still lost.`,
    },
  ].filter(Boolean);

  return { computed, appointed };
}

export function closestAndHighestLoss(matchupRows = []) {
  const games = [];
  matchupRows.forEach((row) => {
    const year = yearOf(row.Year || row.Season);
    const week = yearOf(row.Week);
    const team1 = normalizeBureauName(row['Team1 Manager']);
    const team2 = normalizeBureauName(row['Team2 Manager']);
    const score1 = num(row['Team1 Score']);
    const score2 = num(row['Team2 Score']);
    if (!year || !week || score1 === null || score2 === null) return;
    const margin = round(Math.abs(score1 - score2), 2);
    const winner = score1 > score2 ? team1 : team2;
    const loser = score1 > score2 ? team2 : team1;
    const winnerScore = Math.max(score1, score2);
    const loserScore = Math.min(score1, score2);
    games.push({ year, week, winner, loser, winnerScore, loserScore, margin });
  });
  const closestLoss = games.filter((game) => game.margin > 0).sort((a, b) => a.margin - b.margin)[0] || null;
  const highestScoringLoss = games.slice().sort((a, b) => b.loserScore - a.loserScore)[0] || null;
  return { closestLoss, highestScoringLoss };
}

export function buildBureau({ matchups = [], standings = [], championships = [] }) {
  const luck = buildLuckTribunal(matchups);
  const sacko = buildSackoLineage(standings);
  const curse = buildCurseBoard(standings, championships);
  const { closestLoss, highestScoringLoss } = closestAndHighestLoss(matchups);
  const awards = buildAwardsShow({ luck, sacko, curse, closestLoss, highestScoringLoss });
  return { luck, sacko, curse, awards, closestLoss, highestScoringLoss };
}

async function loadCsv(path, fetchImpl) {
  const response = await fetchImpl(path);
  if (!response.ok) throw new Error(`Failed to load ${path}`);
  const text = await response.text();
  if (!text || /^\s*<!doctype html/i.test(text)) return [];
  return parseCSV(text);
}

export async function loadBureau(fetchImpl = fetch) {
  const [matchups, standings, championships] = await Promise.all([
    loadCsv('/data/weekly_matchups.csv', fetchImpl),
    loadCsv('/data/season_standings.csv', fetchImpl),
    loadCsv('/data/championships.csv', fetchImpl),
  ]);
  return buildBureau({ matchups, standings, championships });
}
